import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, BadgeCheck, Clock, XCircle, FileCheck, Loader2,
  User, Building2, Eye, CheckCircle2, AlertCircle, File, Filter, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface VerificationDoc {
  id: string;
  ca_profile_id: string;
  user_id: string;
  document_type: string;
  file_name: string;
  file_url: string;
  file_size: number;
  status: string;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
  ca_profiles?: { name: string; firm_name: string | null; city: string; is_verified: boolean; membership_number: string | null } | null;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  membership_certificate: "CA Membership Certificate",
  pan_card: "PAN Card",
  firm_registration: "Firm Registration",
};

const statusConfig: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  approved: { label: "Approved", cls: "bg-green-500/10 text-green-600 border-green-500/20" },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive border-destructive/20" },
};

const CAVerificationPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [docs, setDocs] = useState<VerificationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [reviewDoc, setReviewDoc] = useState<VerificationDoc | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    setLoading(true);
    setFetchError(null);
    const { data, error } = await supabase
      .from("ca_verification_documents")
      .select("*, ca_profiles(name, firm_name, city, is_verified, membership_number)")
      .order("created_at", { ascending: false }) as any;
    if (error) {
      setFetchError(error.message || "Failed to load documents");
      toast({ title: "Failed to load", description: error.message, variant: "destructive" });
    } else {
      setDocs(data || []);
    }
    setLoading(false);
  };

  const filtered = filterStatus === "all" ? docs : docs.filter((d) => d.status === filterStatus);

  // Group by CA profile
  const grouped = filtered.reduce<Record<string, VerificationDoc[]>>((acc, doc) => {
    const key = doc.ca_profile_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(doc);
    return acc;
  }, {});

  const handleReview = async (docId: string, status: "approved" | "rejected") => {
    if (!user) return;
    setProcessing(true);

    const { error } = await supabase
      .from("ca_verification_documents")
      .update({
        status,
        reviewer_notes: reviewNotes.trim() || null,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        updated_at: new Date().toISOString(),
      } as any)
      .eq("id", docId);

    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `Document ${status}` });

      // Check if all 3 docs are approved for this CA → set is_verified = true
      const doc = docs.find((d) => d.id === docId);
      if (doc && status === "approved") {
        const { data: allDocs } = await supabase
          .from("ca_verification_documents")
          .select("status, document_type")
          .eq("ca_profile_id", doc.ca_profile_id) as any;

        if (allDocs) {
          const approved = allDocs.filter((d: any) => d.status === "approved" || d.id === docId);
          const allTypes = ["membership_certificate", "pan_card", "firm_registration"];
          const allApproved = allTypes.every((t) =>
            approved.some((d: any) => d.document_type === t && d.status === "approved") ||
            (doc.document_type === t && status === "approved")
          );

          if (allApproved) {
            await supabase.from("ca_profiles").update({ is_verified: true } as any).eq("id", doc.ca_profile_id);
            toast({ title: "CA Verified!", description: "All documents approved. Verified badge activated." });
          }
        }
      } else if (doc && status === "rejected") {
        // If any document is rejected, revoke CA verified badge
        await supabase.from("ca_profiles").update({ is_verified: false } as any).eq("id", doc.ca_profile_id);
      }

      setReviewDoc(null);
      setReviewNotes("");
      fetchDocs();
    }
    setProcessing(false);
  };

  const stats = {
    pending: docs.filter((d) => d.status === "pending").length,
    approved: docs.filter((d) => d.status === "approved").length,
    rejected: docs.filter((d) => d.status === "rejected").length,
    total: docs.length,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-accent" />
            CA Verification
          </h1>
          <p className="text-sm text-muted-foreground">Review and verify CA documents to grant verified status</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-2.5 text-xs gap-1.5"
            onClick={fetchDocs}
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <Filter className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Docs", count: stats.total, icon: File },
          { label: "Pending Review", count: stats.pending, icon: Clock },
          { label: "Approved", count: stats.approved, icon: FileCheck },
          { label: "Rejected", count: stats.rejected, icon: XCircle },
        ].map((s) => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <s.icon className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">
                  {loading || fetchError ? "—" : s.count}
                </p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Document List / States */}
      {loading ? (
        <div className="py-16 flex flex-col items-center justify-center gap-2">
          <Loader2 className="h-7 w-7 animate-spin text-accent" />
          <p className="text-xs text-muted-foreground">Loading verification documents...</p>
        </div>
      ) : fetchError ? (
        <Card className="bg-destructive/5 border-destructive/20">
          <CardContent className="py-12 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">Failed to Load Documents</h3>
              <p className="text-xs text-muted-foreground max-w-md mx-auto mt-1">{fetchError}</p>
            </div>
            <Button size="sm" variant="outline" onClick={fetchDocs} className="gap-1.5 text-xs">
              <RefreshCw className="h-3 w-3" /> Retry Loading
            </Button>
          </CardContent>
        </Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <ShieldCheck className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-foreground mb-1">No documents to review</h3>
            <p className="text-xs text-muted-foreground">Verification documents will appear here when CAs upload them.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {Object.entries(grouped).map(([caId, caDocsList]) => {
              const ca = caDocsList[0]?.ca_profiles;
              const allApproved = ["membership_certificate", "pan_card", "firm_registration"].every((t) =>
                caDocsList.some((d) => d.document_type === t && d.status === "approved")
              );

              return (
                <motion.div key={caId} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Card className="bg-card border-border">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-foreground">{ca?.name || "CA"}</h3>
                              {allApproved && (
                                <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px] gap-1">
                                  <BadgeCheck className="h-3 w-3" /> Verified
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              {ca?.firm_name && <span>{ca.firm_name}</span>}
                              {ca?.city && <span>• {ca.city}</span>}
                              {ca?.membership_number && <span>• {ca.membership_number}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-2 space-y-2">
                      {caDocsList.map((doc) => {
                        const sts = statusConfig[doc.status] || statusConfig.pending;
                        return (
                          <div key={doc.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                            <File className="h-4 w-4 text-accent flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground">{DOC_TYPE_LABELS[doc.document_type] || doc.document_type}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{doc.file_name}</p>
                              {doc.reviewer_notes && (
                                <p className="text-[10px] text-muted-foreground mt-0.5 italic">Note: {doc.reviewer_notes}</p>
                              )}
                            </div>
                            <Badge variant="outline" className={`text-[10px] ${sts.cls}`}>{sts.label}</Badge>
                            <div className="flex items-center gap-1.5">
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" asChild>
                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                  <Eye className="h-3 w-3" />
                                </a>
                              </Button>
                              {doc.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs gap-1"
                                    onClick={() => { setReviewDoc(doc); setReviewNotes(""); }}
                                  >
                                    Review
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewDoc} onOpenChange={(o) => { if (!o) setReviewDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-accent" />
              Review Document
            </DialogTitle>
          </DialogHeader>
          {reviewDoc && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
                <p className="text-xs font-medium text-foreground">{DOC_TYPE_LABELS[reviewDoc.document_type]}</p>
                <p className="text-[10px] text-muted-foreground">{reviewDoc.file_name}</p>
                <Button size="sm" variant="outline" className="mt-2 text-xs gap-1 h-7" asChild>
                  <a href={reviewDoc.file_url} target="_blank" rel="noopener noreferrer">
                    <Eye className="h-3 w-3" /> View Document
                  </a>
                </Button>
              </div>

              <div>
                <p className="text-xs font-medium text-foreground mb-1.5">Reviewer Notes (optional)</p>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this document..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  className="flex-1 gap-1"
                  onClick={() => handleReview(reviewDoc.id, "approved")}
                  disabled={processing}
                >
                  {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Approve
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-1 text-destructive"
                  onClick={() => handleReview(reviewDoc.id, "rejected")}
                  disabled={processing}
                >
                  {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CAVerificationPage;
